import React, { useRef, useEffect } from "react";

interface CanvasProps {
  wings: { id: string; x: number; y: number; color: string }[];
  width: number;
  height: number;
  onDragEnd: (id: string, x: number, y: number) => void;
  onDragStart: (id: string) => void;
  scaleX: number;
  scaleY: number;
  shipWidth: number;
  shipHeight: number;
  draggable?: boolean;
  onWingClick?: (id: string) => void;
}

const CanvasComponent: React.FC<CanvasProps> = ({
  wings,
  width,
  height,
  onDragEnd,
  onDragStart,
  scaleX,
  scaleY,
  shipWidth,
  shipHeight,
  onWingClick,
  draggable = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw the ship
      ctx.fillStyle = "#5070BC";
      ctx.beginPath();
      ctx.moveTo(20, 0); // Starting point
      ctx.arcTo(width, 0, width, 20, 20); // Top-right corner
      ctx.arcTo(width, height, width - 20, height, 20); // Bottom-right corner
      ctx.arcTo(0, height, 0, height - 20, 20); // Bottom-left corner
      ctx.arcTo(0, 0, 20, 0, 20); // Top-left corner
      ctx.closePath();
      ctx.fill();

      // Draw wings
      wings.forEach((wing) => {
        const canvasX = (wing.x + shipWidth / 2) * scaleX;
        const canvasY = height - wing.y * scaleY;
        ctx.beginPath();
        ctx.arc(canvasX, canvasY, 5, 0, 2 * Math.PI);
        ctx.fillStyle = wing.color;
        ctx.fill();
      });
    };

    draw();
  }, [wings, width, height, scaleX, scaleY, shipWidth]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!draggable) return;
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const wing = wings.find((wing) => {
      const wingCanvasX = (wing.x + shipWidth / 2) * scaleX;
      const wingCanvasY = height - wing.y * scaleY;
      return Math.hypot(wingCanvasX - mouseX, wingCanvasY - mouseY) < 10;
    });

    if (wing) {
      if (onWingClick) {
        onWingClick(wing.id);
      }
      onDragStart(wing.id);
      const handleMouseMove = (e: MouseEvent) => {
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        let newShipX = mouseX / scaleX - shipWidth / 2;
        let newShipY = (height - mouseY) / scaleY;

        // Clamp values
        newShipX = Math.max(-shipWidth / 2, Math.min(shipWidth / 2, newShipX));
        newShipY = Math.max(0, Math.min(shipHeight, newShipY));

        // Round to two decimals
        newShipX = Math.round(newShipX * 100) / 100;
        newShipY = Math.round(newShipY * 100) / 100;

        onDragEnd(wing.id, newShipX, newShipY);
      };

      const handleMouseUp = () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      onMouseDown={draggable ? handleMouseDown : undefined}
    />
  );
};

export default CanvasComponent;
